<div>
  <div class="container-fluid">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h1 class="h2 mb-1 fw-bold">Organization Setup</h1>
            <p class="text-muted mb-0" *ngIf="organization">
              Complete setup for <strong>{{ organization.name }}</strong>
            </p>
          </div>
          <button type="button" class="btn btn-outline-secondary" (click)="skipWizard()">
            <i class="bi bi-x-lg me-2"></i>
            Skip Setup
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Loading organization details...</p>
    </div>

    <!-- Error State -->
    <div *ngIf="!loading && errorMessage" class="alert alert-danger mt-3" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success mt-3 alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''"></button>
    </div>

    <!-- Wizard Content -->
    <div *ngIf="!loading && organization" class="card">
      <!-- Stepper Header -->
      <div class="card-header">
        <div class="d-flex justify-content-between py-3">
          <div
            class="step d-flex flex-column align-items-center cursor-pointer"
            [class.active]="currentStep === 1"
            [class.completed]="currentStep > 1"
            (click)="goToStep(1)"
          >
            <div class="step-number rounded-circle d-flex align-items-center justify-content-center fw-semibold mb-2">
              <i class="bi bi-check" *ngIf="currentStep > 1"></i>
              <span *ngIf="currentStep <= 1">1</span>
            </div>
            <div class="step-label small text-nowrap">Organization Details</div>
          </div>

          <div class="step-connector flex-grow-1 mx-3" [class.completed]="currentStep > 1"></div>

          <div
            class="step d-flex flex-column align-items-center cursor-pointer"
            [class.active]="currentStep === 2"
            [class.completed]="currentStep > 2"
            (click)="goToStep(2)"
          >
            <div class="step-number rounded-circle d-flex align-items-center justify-content-center fw-semibold mb-2">
              <i class="bi bi-check" *ngIf="currentStep > 2"></i>
              <span *ngIf="currentStep <= 2">2</span>
            </div>
            <div class="step-label small text-nowrap">Drone Assignment</div>
          </div>

          <div class="step-connector flex-grow-1 mx-3" [class.completed]="currentStep > 2"></div>

          <div
            class="step d-flex flex-column align-items-center cursor-pointer"
            [class.active]="currentStep === 3"
            [class.completed]="currentStep > 3"
            (click)="goToStep(3)"
          >
            <div class="step-number rounded-circle d-flex align-items-center justify-content-center fw-semibold mb-2">
              <i class="bi bi-check" *ngIf="currentStep > 3"></i>
              <span *ngIf="currentStep <= 3">3</span>
            </div>
            <div class="step-label small text-nowrap">Team Members</div>
          </div>
        </div>
      </div>

      <div class="card-body">
        <!-- Step 1: Organization Details -->
        <div *ngIf="currentStep === 1" class="step-content">
          <h4 class="mb-4">Organization Details</h4>
          <p class="text-muted mb-4">Add optional details to complete your organization profile. All fields are optional.</p>

          <form [formGroup]="detailsForm" (ngSubmit)="saveOrganizationDetails()">
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="phone" class="form-label">Phone Number</label>
                  <input
                    type="tel"
                    class="form-control"
                    id="phone"
                    formControlName="phone"
                    mask="(000) 000-0000"
                    [validation]="false"
                    placeholder="(555) 123-4567"
                  >
                  <div class="form-text">Contact phone for the organization</div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="mb-3">
                  <label for="ein" class="form-label">Tax ID / EIN</label>
                  <input
                    type="text"
                    class="form-control"
                    id="ein"
                    formControlName="ein"
                    placeholder="XX-XXXXXXX"
                  >
                  <div class="form-text">Employer Identification Number (format: XX-XXXXXXX)</div>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label for="notes" class="form-label">Internal Notes</label>
              <textarea
                class="form-control"
                id="notes"
                formControlName="notes"
                rows="3"
                placeholder="Any internal notes about this organization..."
              ></textarea>
              <div class="form-text">These notes are only visible to admins</div>
            </div>

            <div class="d-flex justify-content-between mt-4">
              <button type="button" class="btn btn-outline-secondary" (click)="skipWizard()">
                Skip for Now
              </button>
              <div>
                <button
                  type="submit"
                  class="btn btn-outline-primary me-2"
                  [disabled]="saving"
                >
                  <span *ngIf="saving" class="spinner-border spinner-border-sm me-1"></span>
                  Save
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  (click)="nextStep()"
                  [disabled]="saving"
                >
                  Next
                  <i class="bi bi-arrow-right ms-1"></i>
                </button>
              </div>
            </div>
          </form>
        </div>

        <!-- Step 2: Drone Assignment -->
        <div *ngIf="currentStep === 2" class="step-content">
          <h4 class="mb-4">Drone Assignment</h4>
          <p class="text-muted mb-4">Assign drones from ADN inventory to this organization. You can skip this step if no drones are ready yet.</p>

          <!-- Already Assigned Drones -->
          <div *ngIf="assignedDrones.length > 0" class="card mb-4">
            <div class="card-header bg-success text-white">
              <h6 class="mb-0">
                <i class="bi bi-check-circle me-2"></i>
                Currently Assigned Drones ({{ assignedDrones.length }})
              </h6>
            </div>
            <div class="table-responsive">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Serial Number</th>
                    <th>Name</th>
                    <th>Model</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let drone of assignedDrones">
                    <td><code>{{ drone.serialNumber }}</code></td>
                    <td>{{ drone.name || '-' }}</td>
                    <td>{{ drone.manufacturerName }} {{ drone.modelName }}</td>
                    <td>
                      <span class="badge" [ngClass]="drone.source === 'adn' ? 'bg-primary' : 'bg-secondary'">
                        {{ drone.source === 'adn' ? 'ADN Lease' : 'Org Owned' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Search -->
          <div class="mb-4">
            <label class="form-label fw-semibold">Assign Additional Drones from ADN Inventory</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Search drones by serial number, nickname, or model..."
                [(ngModel)]="droneSearchQuery"
              >
            </div>
          </div>

          <!-- Loading Drones -->
          <div *ngIf="loadingDrones" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <span class="ms-2 text-muted">Loading available drones...</span>
          </div>

          <!-- Drones List -->
          <div *ngIf="!loadingDrones && availableDrones.length > 0" class="drone-list overflow-y-auto mb-4">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th style="width: 40px;"></th>
                    <th>Serial Number</th>
                    <th>Nickname</th>
                    <th>Model</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    *ngFor="let drone of filteredDrones"
                    [class.table-primary]="isDroneSelected(drone.id)"
                    (click)="toggleDroneSelection(drone.id)"
                    style="cursor: pointer;"
                  >
                    <td>
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          [checked]="isDroneSelected(drone.id)"
                          (click)="$event.stopPropagation()"
                          (change)="toggleDroneSelection(drone.id)"
                        >
                      </div>
                    </td>
                    <td><code>{{ drone.serialNumber }}</code></td>
                    <td>{{ drone.nickname || '-' }}</td>
                    <td>{{ drone.manufacturer }} {{ drone.model }}</td>
                    <td>
                      <span class="badge bg-success">{{ drone.status }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div *ngIf="selectedDroneIds.length > 0" class="alert alert-info mt-3">
              <strong>{{ selectedDroneIds.length }}</strong> drone(s) selected
              <button
                type="button"
                class="btn btn-primary btn-sm ms-3"
                (click)="assignSelectedDrones()"
                [disabled]="saving"
              >
                <span *ngIf="saving" class="spinner-border spinner-border-sm me-1"></span>
                Assign Selected
              </button>
            </div>
          </div>

          <!-- No Drones Available -->
          <div *ngIf="!loadingDrones && availableDrones.length === 0" class="alert alert-secondary mt-3">
            <i class="bi bi-info-circle me-2"></i>
            No available drones in ADN inventory. You can skip this step and assign drones later.
          </div>

          <div class="d-flex justify-content-between mt-4">
            <button type="button" class="btn btn-outline-secondary" (click)="previousStep()">
              <i class="bi bi-arrow-left me-1"></i>
              Back
            </button>
            <div>
              <button
                type="button"
                class="btn btn-outline-secondary me-2"
                (click)="nextStep()"
              >
                Skip
              </button>
              <button
                type="button"
                class="btn btn-primary"
                (click)="nextStep()"
              >
                Next
                <i class="bi bi-arrow-right ms-1"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Step 3: Team Members -->
        <div *ngIf="currentStep === 3" class="step-content">
          <h4 class="mb-4">Team Members</h4>
          <p class="text-muted mb-4">Add more users to this organization. Invitation emails will NOT be sent automatically - you can trigger them manually when ready.</p>

          <!-- Existing Team Members -->
          <div *ngIf="existingMembers.length > 0" class="card mb-4">
            <div class="card-header bg-success text-white">
              <h6 class="mb-0">
                <i class="bi bi-people-fill me-2"></i>
                Current Team Members ({{ existingMembers.length }})
              </h6>
            </div>
            <div class="table-responsive">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let member of existingMembers">
                    <td>
                      {{ member.firstName || '' }} {{ member.lastName || '' }}
                      <span *ngIf="!member.firstName && !member.lastName" class="text-muted">-</span>
                    </td>
                    <td>{{ member.email }}</td>
                    <td>
                      <span *ngIf="member.isOwner" class="badge bg-warning text-dark me-1">Owner</span>
                      <span *ngIf="member.isChiefSupervisor" class="badge bg-info me-1">Chief Supervisor</span>
                      <span *ngIf="hasRolePIC(member.combinedRole)" class="badge bg-primary me-1">PIC</span>
                      <span *ngIf="hasRolePilot(member.combinedRole)" class="badge bg-secondary">Pilot</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Loading Members -->
          <div *ngIf="loadingMembers" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <span class="ms-2 text-muted">Loading team members...</span>
          </div>

          <!-- Add Team Member Form -->
          <div class="card mb-4">
            <div class="card-header">
              <h6 class="mb-0">Add Team Member</h6>
            </div>
            <div class="card-body">
              <form [formGroup]="teamMemberForm" (ngSubmit)="addTeamMember()">
                <div class="row">
                  <div class="col-md-4">
                    <div class="mb-3">
                      <label for="memberEmail" class="form-label">Email <span class="text-danger">*</span></label>
                      <input
                        type="email"
                        class="form-control"
                        id="memberEmail"
                        formControlName="email"
                        placeholder="user@example.com"
                      >
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="mb-3">
                      <label for="memberFirstName" class="form-label">First Name</label>
                      <input
                        type="text"
                        class="form-control"
                        id="memberFirstName"
                        formControlName="firstName"
                        placeholder="Optional"
                      >
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="mb-3">
                      <label for="memberLastName" class="form-label">Last Name</label>
                      <input
                        type="text"
                        class="form-control"
                        id="memberLastName"
                        formControlName="lastName"
                        placeholder="Optional"
                      >
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-3">
                      <label for="memberRole" class="form-label">Role <span class="text-danger">*</span></label>
                      <select class="form-select" id="memberRole" formControlName="role">
                        <option *ngFor="let role of availableRoles" [value]="role.value">
                          {{ role.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="d-flex justify-content-end">
                  <button
                    type="submit"
                    class="btn btn-outline-primary"
                    [disabled]="teamMemberForm.invalid || addingMember"
                  >
                    <span *ngIf="addingMember" class="spinner-border spinner-border-sm me-1"></span>
                    <i class="bi bi-plus-lg me-1" *ngIf="!addingMember"></i>
                    Add Member
                  </button>
                </div>
              </form>
            </div>
          </div>

          <!-- Pending Invitations List -->
          <div *ngIf="pendingInvitations.length > 0" class="card mb-4">
            <div class="card-header">
              <h6 class="mb-0">Pending Invitations</h6>
            </div>
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Email Status</th>
                    <th>Created</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let invitation of pendingInvitations">
                    <td>{{ invitation.email }}</td>
                    <td>{{ getRoleLabel(invitation.role) }}</td>
                    <td>
                      <span
                        class="badge"
                        [ngClass]="getEmailStatusBadgeClass(invitation.emailStatus)"
                      >
                        {{ getEmailStatusLabel(invitation.emailStatus) }}
                      </span>
                    </td>
                    <td>{{ formatDate(invitation.createdAt) }}</td>
                    <td class="text-end">
                      <button
                        *ngIf="invitation.emailStatus !== 'sent'"
                        type="button"
                        class="btn btn-sm btn-outline-primary"
                        (click)="sendInvitationEmail(invitation.id)"
                      >
                        <i class="bi bi-envelope me-1"></i>
                        Send Email
                      </button>
                      <button
                        *ngIf="invitation.emailStatus === 'sent'"
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        (click)="sendInvitationEmail(invitation.id)"
                      >
                        <i class="bi bi-arrow-repeat me-1"></i>
                        Resend
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- No Invitations -->
          <div *ngIf="pendingInvitations.length === 0" class="alert alert-secondary mt-3">
            <i class="bi bi-info-circle me-2"></i>
            No pending invitations. Add team members using the form above.
          </div>

          <div class="d-flex justify-content-between mt-4">
            <button type="button" class="btn btn-outline-secondary" (click)="previousStep()">
              <i class="bi bi-arrow-left me-1"></i>
              Back
            </button>
            <button type="button" class="btn btn-success" (click)="finishWizard()">
              <i class="bi bi-check-lg me-1"></i>
              Finish Setup
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
